/**
 * Parse a user-supplied URL into an embeddable representation for the video
 * block. Handles YouTube (watch / shorts / embed / youtu.be), Vimeo, and
 * direct video files (mp4 / webm / mov / m3u8).
 *
 * When `startSeconds` is provided the platform-appropriate offset is appended
 * to the embed URL: YouTube → `?start=N`, Vimeo → `#t=Ns`, native files →
 * `#t=N` (the media fragments spec handled natively by <video>). If the user
 * pasted a YouTube URL that already includes `t=` (the "Share at current
 * time" output), it's honored as the default and only overridden when the
 * caller passes a non-zero `startSeconds`.
 *
 * Returns:
 *   - { kind: 'iframe', src }  → render <iframe src={src} />
 *   - { kind: 'video', src }   → render <video src={src} controls />
 *   - null                     → not recognized
 */
export type EmbedInfo =
  | { kind: 'iframe'; src: string }
  | { kind: 'video'; src: string }
  | null;

export function parseVideoUrl(url: string | undefined | null, startSeconds?: number | null): EmbedInfo {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const startNum = typeof startSeconds === 'number' && Number.isFinite(startSeconds) && startSeconds > 0
    ? Math.floor(startSeconds)
    : 0;

  // ─ YouTube ──────────────────────────────────────────────
  //   https://www.youtube.com/watch?v=ID
  //   https://youtu.be/ID
  //   https://www.youtube.com/embed/ID
  //   https://www.youtube.com/shorts/ID
  const yt =
    u.match(/[?&]v=([A-Za-z0-9_-]{6,})/) ||
    u.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ||
    u.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/) ||
    u.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (yt) {
    let effectiveStart = startNum;
    if (!effectiveStart) {
      const urlT = u.match(/[?&#]t=(\d+)/) || u.match(/[?&]start=(\d+)/);
      if (urlT) effectiveStart = parseInt(urlT[1], 10) || 0;
    }
    const base = `https://www.youtube.com/embed/${yt[1]}`;
    return { kind: 'iframe', src: effectiveStart ? `${base}?start=${effectiveStart}` : base };
  }

  // ─ Vimeo ─────────────────────────────────────────────────
  //   https://vimeo.com/12345678
  //   https://player.vimeo.com/video/12345678
  const vim =
    u.match(/vimeo\.com\/(?:video\/)?(\d+)/) ||
    u.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (vim) {
    const base = `https://player.vimeo.com/video/${vim[1]}`;
    return { kind: 'iframe', src: startNum ? `${base}#t=${startNum}s` : base };
  }

  // ─ Direct video files ────────────────────────────────────
  if (/\.(mp4|webm|mov|m3u8)(\?|$)/i.test(u)) {
    // Media-fragments URI: browsers seek to N seconds on load when the URL
    // ends with #t=N. Strip any existing #t= the user may have pasted.
    const cleaned = u.replace(/#t=[^&]*$/i, '');
    return { kind: 'video', src: startNum ? `${cleaned}#t=${startNum}` : cleaned };
  }

  // ─ Embed-ready URL passed through as-is (e.g. a custom iframe URL)
  if (/^https?:\/\/.+\/embed\//i.test(u)) {
    if (!startNum) return { kind: 'iframe', src: u };
    const sep = u.includes('?') ? '&' : '?';
    return { kind: 'iframe', src: `${u}${sep}start=${startNum}` };
  }

  return null;
}
